import React, { useState, useEffect } from 'react';
import Modal from '@components/common/Modal/Modal';
import Input from '@components/Form/Input/Input';
import Select from '@components/Form/Select/Select';
import Textarea from '@components/Form/Textarea/Textarea';
import Button from '@components/Button/Button';
import Toggle from '@components/common/Toggle';
import toast from 'react-hot-toast';
import { codingAdminService } from '@/services/coding/codingAdminService';
import type {
  CodingProblemAdminData,
  CreateProblemPayload,
  TestCaseItem,
  ProblemDataset,
  StarterCell,
  NotebookAssertion,
} from '@/services/coding/codingAdminService';
import {
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Play,
  Code2,
  FileSpreadsheet,
  UploadCloud,
  FileText,
  ExternalLink,
} from 'lucide-react';

export interface CodingProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  cohortId: string;
  problem?: CodingProblemAdminData | null;
  onSuccess: () => void;
}

const DEFAULT_STARTER_CODE = `def solution(nums, target):
    # Write your solution here
    pass
`;

const DEFAULT_NOTEBOOK_CELLS: StarterCell[] = [
  {
    id: 'cell-md-1',
    type: 'markdown',
    content: `# Machine Learning Task\n\n### Objective:\nLoad the attached dataset, preprocess the features, train your model, and achieve the required evaluation accuracy.\n\n### Instructions:\n1. Use \`pd.read_csv('dataset.csv')\` to load the data.\n2. Clean and prepare training/test sets.\n3. Fit your model and store it in variable \`model\`.\n4. Calculate test accuracy / R² score into \`score\`.`,
  },
  {
    id: 'cell-code-1',
    type: 'code',
    content: `import pandas as pd
import numpy as np
from sklearn.model_selection import train_test_split
from sklearn.ensemble import RandomForestClassifier

# Load the attached dataset from virtual filesystem
# df = pd.read_csv('dataset.csv')
# print(df.head())
`,
  },
];

const DEFAULT_NOTEBOOK_ASSERTIONS: NotebookAssertion[] = [
  {
    name: 'Check dataset loaded & cleaned',
    assertion_code: "assert 'df' in globals(), 'DataFrame df not found in global namespace'",
    points: 20,
    is_hidden: false,
    explanation: 'Ensure you assign the loaded data to variable `df`.',
  },
  {
    name: 'Check model trained',
    assertion_code: "assert 'model' in globals() and hasattr(model, 'predict'), 'Trained model object not found'",
    points: 40,
    is_hidden: false,
    explanation: 'Ensure your trained Scikit-Learn model is stored in variable `model`.',
  },
  {
    name: 'Check accuracy threshold (>= 80%)',
    assertion_code: "assert ('score' in globals() and score >= 0.8) or ('accuracy' in globals() and accuracy >= 0.8), 'Model accuracy score is below 80%'",
    points: 40,
    is_hidden: false,
    explanation: 'Model must achieve at least 80% accuracy / score on test data.',
  },
];

const CodingProblemModal: React.FC<CodingProblemModalProps> = ({
  isOpen,
  onClose,
  cohortId,
  problem,
  onSuccess,
}) => {
  const isEditing = !!problem;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState<'EASY' | 'MEDIUM' | 'HARD'>('EASY');
  const [problemType, setProblemType] = useState<'FUNCTION' | 'STDIN_STDOUT' | 'NOTEBOOK'>('FUNCTION');
  const [functionName, setFunctionName] = useState('solution');
  const [starterCode, setStarterCode] = useState(DEFAULT_STARTER_CODE);
  const [inputFormat, setInputFormat] = useState('');
  const [outputFormat, setOutputFormat] = useState('');
  const [constraints, setConstraints] = useState('');
  const [timeLimitMs, setTimeLimitMs] = useState(2000);
  const [memoryLimitMb, setMemoryLimitMb] = useState(128);
  const [comparisonType, setComparisonType] = useState<'EXACT' | 'WHITESPACE_NORMALIZED' | 'JSON_EQUIVALENT'>('JSON_EQUIVALENT');
  const [referenceSolution, setReferenceSolution] = useState('');
  const [weekNumber, setWeekNumber] = useState<number | undefined>(1);
  const [dayNumber, setDayNumber] = useState<number | undefined>(1);
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');

  // Algorithmic test cases
  const [testCases, setTestCases] = useState<TestCaseItem[]>([
    { input: '{"nums": [2, 7, 11, 15], "target": 9}', expected_output: '[0, 1]', is_hidden: false, explanation: 'Sample case 1' },
    { input: '{"nums": [3, 2, 4], "target": 6}', expected_output: '[1, 2]', is_hidden: false, explanation: 'Sample case 2' },
    { input: '{"nums": [3, 3], "target": 6}', expected_output: '[0, 1]', is_hidden: true },
  ]);

  // Notebook / ML Task fields
  const [datasets, setDatasets] = useState<ProblemDataset[]>([]);
  const [starterCells, setStarterCells] = useState<StarterCell[]>(DEFAULT_NOTEBOOK_CELLS);
  const [notebookAssertions, setNotebookAssertions] = useState<NotebookAssertion[]>(DEFAULT_NOTEBOOK_ASSERTIONS);
  const [isUploadingDataset, setIsUploadingDataset] = useState(false);

  const [activeTab, setActiveTab] = useState<'details' | 'code' | 'datasets' | 'testcases' | 'solution'>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingRef, setIsValidatingRef] = useState(false);
  const [refValidationResult, setRefValidationResult] = useState<{ is_valid: boolean; message: string } | null>(null);

  useEffect(() => {
    if (problem) {
      setTitle(problem.title || '');
      setDescription(problem.description || '');
      setDifficulty(problem.difficulty || 'EASY');
      setProblemType(problem.problem_type || 'FUNCTION');
      setFunctionName(problem.function_name || 'solution');
      setStarterCode(problem.starter_code || DEFAULT_STARTER_CODE);
      setInputFormat(problem.input_format || '');
      setOutputFormat(problem.output_format || '');
      setConstraints(problem.constraints || '');
      setTimeLimitMs(problem.time_limit_ms || 2000);
      setMemoryLimitMb(problem.memory_limit_mb || 128);
      setComparisonType(problem.comparison_type || 'JSON_EQUIVALENT');
      setReferenceSolution(problem.reference_solution || '');
      setWeekNumber(problem.week_number || 1);
      setDayNumber(problem.day_number || 1);
      setStatus(problem.status || 'ACTIVE');
      setTestCases(
        problem.test_cases && problem.test_cases.length > 0
          ? problem.test_cases.map((tc) => ({
              id: tc.id,
              input: tc.input,
              expected_output: tc.expected_output,
              is_hidden: tc.is_hidden,
              order_no: tc.order_no,
              explanation: tc.explanation || '',
            }))
          : [{ input: '', expected_output: '', is_hidden: false }]
      );
      setDatasets(problem.datasets || []);
      setStarterCells(
        problem.starter_cells && problem.starter_cells.length > 0
          ? problem.starter_cells
          : DEFAULT_NOTEBOOK_CELLS
      );
      setNotebookAssertions(
        problem.notebook_assertions && problem.notebook_assertions.length > 0
          ? problem.notebook_assertions
          : DEFAULT_NOTEBOOK_ASSERTIONS
      );
    } else {
      setTitle('');
      setDescription('');
      setDifficulty('EASY');
      setProblemType('FUNCTION');
      setFunctionName('solution');
      setStarterCode(DEFAULT_STARTER_CODE);
      setInputFormat('');
      setOutputFormat('');
      setConstraints('');
      setTimeLimitMs(2000);
      setMemoryLimitMb(128);
      setComparisonType('JSON_EQUIVALENT');
      setReferenceSolution('');
      setWeekNumber(1);
      setDayNumber(1);
      setStatus('ACTIVE');
      setTestCases([
        { input: '{"nums": [2, 7, 11, 15], "target": 9}', expected_output: '[0, 1]', is_hidden: false, explanation: 'Sample case 1' },
        { input: '{"nums": [3, 2, 4], "target": 6}', expected_output: '[1, 2]', is_hidden: false, explanation: 'Sample case 2' },
        { input: '{"nums": [3, 3], "target": 6}', expected_output: '[0, 1]', is_hidden: true },
      ]);
      setDatasets([]);
      setStarterCells(DEFAULT_NOTEBOOK_CELLS);
      setNotebookAssertions(DEFAULT_NOTEBOOK_ASSERTIONS);
    }
    setRefValidationResult(null);
    setActiveTab('details');
  }, [problem, isOpen]);

  // Dataset Upload Handler
  const handleDatasetFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingDataset(true);
      const res = await codingAdminService.uploadDatasetFile(file);
      const uploadedData = res.data.data;

      setDatasets((prev) => [
        ...prev,
        {
          name: uploadedData.original_name,
          file_path: uploadedData.file_path,
          size_bytes: uploadedData.size_bytes,
          mime_type: uploadedData.mime_type,
          description: `Uploaded dataset: ${uploadedData.original_name}`,
        },
      ]);
      toast.success(`Dataset "${file.name}" uploaded successfully!`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to upload dataset file');
    } finally {
      setIsUploadingDataset(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveDataset = async (index: number) => {
    const dataset = datasets[index];
    try {
      const filename = dataset.file_path.split('/').pop();
      if (filename) {
        await codingAdminService.deleteDatasetFile(filename);
      }
    } catch (err) {
      // Continue removing from UI even if backend delete has an issue
    }
    setDatasets((prev) => prev.filter((_, i) => i !== index));
    toast.success('Dataset removed');
  };

  // Starter Cells Management
  const handleAddStarterCell = (type: 'code' | 'markdown') => {
    setStarterCells((prev) => [
      ...prev,
      {
        id: `cell-${Date.now()}`,
        type,
        content: type === 'code' ? '# Write Python code here\n' : '### Instructions\n',
      },
    ]);
  };

  const handleRemoveStarterCell = (index: number) => {
    if (starterCells.length <= 1) {
      toast.error('Notebook must have at least one cell');
      return;
    }
    setStarterCells((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateStarterCell = (index: number, content: string) => {
    setStarterCells((prev) =>
      prev.map((c, i) => (i === index ? { ...c, content } : c))
    );
  };

  // Notebook Assertions Management
  const handleAddAssertion = () => {
    setNotebookAssertions((prev) => [
      ...prev,
      {
        name: 'New Test Assertion',
        assertion_code: "assert 'variable_name' in globals(), 'Variable missing'",
        points: 20,
        is_hidden: false,
        explanation: 'Check variable presence or model accuracy.',
      },
    ]);
  };

  const handleRemoveAssertion = (index: number) => {
    if (notebookAssertions.length <= 1) {
      toast.error('Task must have at least one test assertion');
      return;
    }
    setNotebookAssertions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateAssertion = (index: number, field: keyof NotebookAssertion, value: any) => {
    setNotebookAssertions((prev) =>
      prev.map((a, i) => (i === index ? { ...a, [field]: value } : a))
    );
  };

  // Algorithmic Test Cases Management
  const handleAddTestCase = () => {
    setTestCases((prev) => [
      ...prev,
      { input: '', expected_output: '', is_hidden: false, explanation: '' },
    ]);
  };

  const handleRemoveTestCase = (index: number) => {
    if (testCases.length <= 1) {
      toast.error('Problem must have at least one test case.');
      return;
    }
    setTestCases((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateTestCase = (index: number, field: keyof TestCaseItem, value: any) => {
    setTestCases((prev) =>
      prev.map((tc, i) => (i === index ? { ...tc, [field]: value } : tc))
    );
  };

  const handleValidateReferenceSolution = async () => {
    if (!referenceSolution.trim()) {
      toast.error('Please enter a reference solution first.');
      return;
    }
    if (testCases.some((tc) => !tc.input.trim() || !tc.expected_output.trim())) {
      toast.error('Please make sure all test cases have input and expected output.');
      return;
    }

    try {
      setIsValidatingRef(true);
      if (problem?.id) {
        const formattedTestCases = testCases.map((tc, idx) => ({
          id: tc.id,
          input: tc.input.trim(),
          expected_output: tc.expected_output.trim(),
          is_hidden: !!tc.is_hidden,
          order_no: idx + 1,
          explanation: tc.explanation?.trim() || undefined,
        }));

        await codingAdminService.updateProblem(problem.id, {
          reference_solution: referenceSolution.trim(),
          function_name: functionName.trim() || undefined,
          problem_type: problemType,
          test_cases: formattedTestCases,
          time_limit_ms: Number(timeLimitMs) || 2000,
          comparison_type: comparisonType,
        });
        const res = await codingAdminService.validateReference(problem.id);
        const passed = res.data.data.is_valid;
        setRefValidationResult({
          is_valid: passed,
          message: passed
            ? `All ${res.data.data.total_count} test cases passed successfully!`
            : `Failed: ${res.data.data.first_error || 'One or more test cases did not match'}`,
        });
        if (passed) toast.success('Reference solution verified successfully!');
        else toast.error('Reference solution failed validation.');
      } else {
        toast.success('Save the problem to run full backend validation.');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to validate reference solution');
    } finally {
      setIsValidatingRef(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Problem title is required');
      setActiveTab('details');
      return;
    }
    if (!description.trim()) {
      toast.error('Problem description is required');
      setActiveTab('details');
      return;
    }

    if (problemType === 'NOTEBOOK') {
      if (starterCells.length === 0) {
        toast.error('Notebook must contain at least one starter cell');
        setActiveTab('code');
        return;
      }
      if (notebookAssertions.length === 0) {
        toast.error('Notebook must contain at least one validation assertion');
        setActiveTab('testcases');
        return;
      }
    } else {
      if (!starterCode.trim()) {
        toast.error('Starter code is required');
        setActiveTab('code');
        return;
      }
      if (testCases.some((tc) => !tc.input.trim() || !tc.expected_output.trim())) {
        toast.error('All test cases must have both input and expected output defined');
        setActiveTab('testcases');
        return;
      }
    }

    const payload: CreateProblemPayload = {
      cohort_id: cohortId,
      title: title.trim(),
      description: description.trim(),
      difficulty,
      language: 'python',
      problem_type: problemType,
      starter_code: problemType === 'NOTEBOOK' ? '' : starterCode,
      function_name: problemType === 'FUNCTION' ? (functionName.trim() || 'solution') : undefined,
      input_format: inputFormat.trim() || undefined,
      output_format: outputFormat.trim() || undefined,
      constraints: constraints.trim() || undefined,
      time_limit_ms: Number(timeLimitMs) || 2000,
      memory_limit_mb: Number(memoryLimitMb) || 128,
      comparison_type: comparisonType,
      reference_solution: referenceSolution.trim() || undefined,
      status,
      week_number: weekNumber ? Number(weekNumber) : undefined,
      day_number: dayNumber ? Number(dayNumber) : undefined,
      datasets: problemType === 'NOTEBOOK' ? datasets : undefined,
      starter_cells: problemType === 'NOTEBOOK' ? starterCells : undefined,
      notebook_assertions: problemType === 'NOTEBOOK' ? notebookAssertions : undefined,
      test_cases:
        problemType !== 'NOTEBOOK'
          ? testCases.map((tc, idx) => ({
              input: tc.input.trim(),
              expected_output: tc.expected_output.trim(),
              is_hidden: !!tc.is_hidden,
              order_no: idx + 1,
              explanation: tc.explanation?.trim() || undefined,
            }))
          : [],
    };

    try {
      setIsSubmitting(true);
      if (isEditing && problem) {
        await codingAdminService.updateProblem(problem.id, payload);
        toast.success('Task updated successfully');
      } else {
        await codingAdminService.createProblem(cohortId, payload);
        toast.success('Task created successfully');
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNotebook = problemType === 'NOTEBOOK';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit Task: ${problem?.title}` : 'Create New Coding / ML Task'}
      maxWidth="max-w-[900px]"
    >
      <form onSubmit={handleSubmit} className="flex flex-col max-h-[82vh] overflow-hidden">
        {/* Navigation Tabs inside Modal */}
        <div className="flex border-b border-gray-200 bg-gray-50 px-4 pt-2 gap-2 shrink-0 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`px-3.5 py-2 text-xs font-bold rounded-t-lg transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === 'details'
                ? 'bg-white text-[var(--primary-color)] border-t border-x border-gray-200 shadow-xs'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            1. Task Details
          </button>

          {isNotebook ? (
            <>
              <button
                type="button"
                onClick={() => setActiveTab('datasets')}
                className={`px-3.5 py-2 text-xs font-bold rounded-t-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === 'datasets'
                    ? 'bg-white text-[var(--primary-color)] border-t border-x border-gray-200 shadow-xs'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <FileSpreadsheet size={13} />
                2. Datasets ({datasets.length})
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('code')}
                className={`px-3.5 py-2 text-xs font-bold rounded-t-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === 'code'
                    ? 'bg-white text-[var(--primary-color)] border-t border-x border-gray-200 shadow-xs'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <Code2 size={13} />
                3. Starter Notebook ({starterCells.length} cells)
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('testcases')}
                className={`px-3.5 py-2 text-xs font-bold rounded-t-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === 'testcases'
                    ? 'bg-white text-[var(--primary-color)] border-t border-x border-gray-200 shadow-xs'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <CheckCircle2 size={13} />
                4. Test Assertions ({notebookAssertions.length})
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setActiveTab('code')}
                className={`px-3.5 py-2 text-xs font-bold rounded-t-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'code'
                    ? 'bg-white text-[var(--primary-color)] border-t border-x border-gray-200 shadow-xs'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                2. Starter Code & Types
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('testcases')}
                className={`px-3.5 py-2 text-xs font-bold rounded-t-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === 'testcases'
                    ? 'bg-white text-[var(--primary-color)] border-t border-x border-gray-200 shadow-xs'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                3. Test Cases ({testCases.length})
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('solution')}
                className={`px-3.5 py-2 text-xs font-bold rounded-t-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'solution'
                    ? 'bg-white text-[var(--primary-color)] border-t border-x border-gray-200 shadow-xs'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                4. Reference Solution
              </button>
            </>
          )}
        </div>

        {/* Tab Content */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* TAB 1: Problem Details */}
          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* Task Category / Type Banner */}
              <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                <label className="text-xs font-bold text-navy block mb-1.5">Task Format & Execution Type</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                  <div
                    onClick={() => setProblemType('NOTEBOOK')}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      problemType === 'NOTEBOOK'
                        ? 'border-primary bg-white shadow-sm ring-1 ring-primary/30'
                        : 'border-slate-200 bg-white/70 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-bold text-xs text-navy">
                      <FileSpreadsheet size={15} className="text-primary" />
                      ML / Jupyter Colab
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Data Science & ML with attached dataset files, Pandas/Scikit-Learn, and assertion tests.
                    </p>
                  </div>

                  <div
                    onClick={() => setProblemType('FUNCTION')}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      problemType === 'FUNCTION'
                        ? 'border-primary bg-white shadow-sm ring-1 ring-primary/30'
                        : 'border-slate-200 bg-white/70 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-bold text-xs text-navy">
                      <Code2 size={15} className="text-primary" />
                      Function (LeetCode)
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Algorithm problem where student implements a specific Python function.
                    </p>
                  </div>

                  <div
                    onClick={() => setProblemType('STDIN_STDOUT')}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      problemType === 'STDIN_STDOUT'
                        ? 'border-primary bg-white shadow-sm ring-1 ring-primary/30'
                        : 'border-slate-200 bg-white/70 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-bold text-xs text-navy">
                      <FileText size={15} className="text-primary" />
                      Standard I/O (stdin)
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Competitive coding style using standard console input and print output.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-1 md:col-span-2">
                  <Input
                    label="Task Title"
                    placeholder="e.g. Housing Price Prediction with Random Forest"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                <Select
                  label="Difficulty Level"
                  options={[
                    { value: 'EASY', label: 'Easy' },
                    { value: 'MEDIUM', label: 'Medium' },
                    { value: 'HARD', label: 'Hard' },
                  ]}
                  value={difficulty}
                  onChange={(val) => setDifficulty(val as any)}
                  required
                />

                <div className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-gray-700">Status</span>
                    <span className="text-[11px] text-gray-400">
                      {status === 'ACTIVE' ? 'Active & visible in cohort' : 'Inactive / Draft'}
                    </span>
                  </div>
                  <Toggle
                    active={status === 'ACTIVE'}
                    onChange={(active) => setStatus(active ? 'ACTIVE' : 'INACTIVE')}
                  />
                </div>

                <Select
                  label="Assigned Week (Optional)"
                  options={[
                    { value: '1', label: 'Week 1' },
                    { value: '2', label: 'Week 2' },
                    { value: '3', label: 'Week 3' },
                    { value: '4', label: 'Week 4' },
                  ]}
                  value={weekNumber ? String(weekNumber) : ''}
                  onChange={(val) => setWeekNumber(val ? Number(val) : undefined)}
                />

                <Select
                  label="Assigned Day (Optional)"
                  options={[
                    { value: '1', label: 'Day 1' },
                    { value: '2', label: 'Day 2' },
                    { value: '3', label: 'Day 3' },
                    { value: '4', label: 'Day 4' },
                    { value: '5', label: 'Day 5' },
                    { value: '6', label: 'Day 6' },
                    { value: '7', label: 'Day 7' },
                  ]}
                  value={dayNumber ? String(dayNumber) : ''}
                  onChange={(val) => setDayNumber(val ? Number(val) : undefined)}
                />
              </div>

              <div>
                <Textarea
                  label="Problem Description & Instructions (Markdown supported)"
                  placeholder="Describe the machine learning task, datasets provided, objectives, and evaluation requirements..."
                  rows={7}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>

              {!isNotebook && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Textarea
                    label="Constraints"
                    placeholder="e.g. 2 <= nums.length <= 10^4"
                    rows={3}
                    value={constraints}
                    onChange={(e) => setConstraints(e.target.value)}
                  />
                  <div className="space-y-2">
                    <Input
                      label="Input Format (Optional)"
                      placeholder="e.g. nums: List[int], target: int"
                      value={inputFormat}
                      onChange={(e) => setInputFormat(e.target.value)}
                    />
                    <Input
                      label="Output Format (Optional)"
                      placeholder="e.g. List[int]"
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: Attached Datasets (For NOTEBOOK problems) */}
          {activeTab === 'datasets' && isNotebook && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-navy flex items-center gap-1.5">
                      <UploadCloud size={16} className="text-primary" />
                      Upload Task Datasets
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Upload CSV, XLSX, JSON, Parquet, or ZIP files. Files are automatically mounted into Pyodide Virtual File System for students.
                    </p>
                  </div>

                  <label className="flex items-center gap-1.5 px-3.5 py-2 bg-primary text-white text-xs font-semibold rounded-xl hover:bg-primary/90 transition-all cursor-pointer shadow-xs">
                    <Plus size={14} />
                    {isUploadingDataset ? 'Uploading...' : 'Upload Dataset File'}
                    <input
                      type="file"
                      accept=".csv,.tsv,.xlsx,.xls,.json,.parquet,.txt,.zip,.gz"
                      onChange={handleDatasetFileUpload}
                      disabled={isUploadingDataset}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {datasets.length === 0 ? (
                <div className="text-center p-8 border-2 border-dashed border-slate-200 rounded-2xl bg-white space-y-2">
                  <FileSpreadsheet size={32} className="mx-auto text-slate-300" />
                  <p className="text-xs font-medium text-slate-600">No datasets uploaded yet</p>
                  <p className="text-[11px] text-slate-400">
                    Click "Upload Dataset File" above to attach CSV, JSON, or Excel files for this ML task.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {datasets.map((dataset, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 rounded-xl border border-slate-200 bg-white flex items-center justify-between shadow-xs hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-xs">
                          📊
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-xs text-navy">{dataset.name}</span>
                            {dataset.size_bytes && (
                              <span className="text-[10px] text-slate-400">
                                ({Math.round(dataset.size_bytes / 1024)} KB)
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                            Python snippet: <code className="bg-slate-100 px-1 py-0.5 rounded text-primary">pd.read_csv('{dataset.name}')</code>
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {dataset.file_path && (
                          <a
                            href={dataset.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-slate-400 hover:text-primary transition-colors cursor-pointer"
                            title="Preview / Download file"
                          >
                            <ExternalLink size={15} />
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveDataset(idx)}
                          className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors cursor-pointer"
                          title="Remove dataset"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2 / 3: Starter Code & Types */}
          {activeTab === 'code' && (
            <div className="space-y-4">
              {isNotebook ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-blue-50/60 p-3.5 rounded-xl border border-blue-200">
                    <div>
                      <h4 className="text-xs font-bold text-navy">Starter Notebook Cells</h4>
                      <p className="text-[11px] text-slate-500">
                        Configure the default Markdown instructions and starter Python code pre-loaded for students.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleAddStarterCell('markdown')}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer flex items-center gap-1"
                      >
                        <Plus size={12} /> + Text Cell
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddStarterCell('code')}
                        className="px-2.5 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary/90 cursor-pointer flex items-center gap-1"
                      >
                        <Plus size={12} /> + Code Cell
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {starterCells.map((cell, idx) => (
                      <div key={cell.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-xs">
                          <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                            {cell.type === 'code' ? (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono text-[10px] font-bold">
                                Python Code
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-mono text-[10px] font-bold">
                                Markdown Text
                              </span>
                            )}
                            Cell #{idx + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveStarterCell(idx)}
                            className="text-slate-400 hover:text-rose-500 p-1 cursor-pointer"
                            title="Remove cell"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <textarea
                          rows={Math.max(3, cell.content.split('\n').length + 1)}
                          value={cell.content}
                          onChange={(e) => handleUpdateStarterCell(idx, e.target.value)}
                          className={`w-full p-3 font-mono text-xs focus:outline-none ${
                            cell.type === 'code'
                              ? 'bg-slate-900 text-emerald-400'
                              : 'bg-slate-50 text-slate-800'
                          }`}
                          placeholder={
                            cell.type === 'code'
                              ? '# Enter starter Python code here'
                              : '### Enter markdown instructions here'
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Execution Type"
                      options={[
                        { value: 'FUNCTION', label: 'Function-Based (LeetCode Style)' },
                        { value: 'STDIN_STDOUT', label: 'Standard I/O (stdin / stdout)' },
                      ]}
                      value={problemType}
                      onChange={(val) => setProblemType(val as any)}
                      required
                    />

                    {problemType === 'FUNCTION' ? (
                      <Input
                        label="Python Function Name"
                        placeholder="e.g. two_sum"
                        value={functionName}
                        onChange={(e) => setFunctionName(e.target.value)}
                        required
                      />
                    ) : (
                      <div className="text-xs text-gray-500 pt-7">
                        User script reads input via <code>input()</code> and prints results via <code>print()</code>.
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      type="number"
                      label="Time Limit (ms)"
                      min={500}
                      max={10000}
                      value={String(timeLimitMs)}
                      onChange={(e) => setTimeLimitMs(Number(e.target.value))}
                      required
                    />
                    <Input
                      type="number"
                      label="Memory Limit (MB)"
                      min={32}
                      max={512}
                      value={String(memoryLimitMb)}
                      onChange={(e) => setMemoryLimitMb(Number(e.target.value))}
                      required
                    />
                    <Select
                      label="Comparison Strategy"
                      options={[
                        { value: 'JSON_EQUIVALENT', label: 'JSON / List Equivalent' },
                        { value: 'EXACT', label: 'Exact String Match' },
                        { value: 'WHITESPACE_NORMALIZED', label: 'Whitespace Normalized' },
                      ]}
                      value={comparisonType}
                      onChange={(val) => setComparisonType(val as any)}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1.5 flex items-center gap-1.5">
                      <Code2 size={14} className="text-primary" />
                      Starter Code (Python 3)
                    </label>
                    <textarea
                      rows={8}
                      value={starterCode}
                      onChange={(e) => setStarterCode(e.target.value)}
                      className="w-full font-mono text-xs p-3 bg-slate-900 text-emerald-400 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="def solution():&#10;    pass"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3 / 4: Test Cases & Assertions */}
          {activeTab === 'testcases' && (
            <div className="space-y-4">
              {isNotebook ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-blue-50/50 p-3 rounded-xl border border-blue-200">
                    <div>
                      <h4 className="text-xs font-bold text-navy">Machine Learning Test Assertions</h4>
                      <p className="text-[11px] text-slate-500">
                        Define automated Python assertions evaluated against the student's notebook namespace.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      className="text-xs py-1.5 px-3 flex items-center gap-1 cursor-pointer"
                      onClick={handleAddAssertion}
                    >
                      <Plus size={14} />
                      Add Assertion
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {notebookAssertions.map((assertion, idx) => (
                      <div
                        key={idx}
                        className={`border rounded-xl p-4 space-y-3 bg-white ${
                          assertion.is_hidden ? 'border-amber-200 bg-amber-50/20' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-navy px-2 py-0.5 bg-gray-100 rounded-md">
                              Check #{idx + 1}
                            </span>
                            <span className="text-xs font-semibold text-slate-700">{assertion.name}</span>
                          </div>

                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!assertion.is_hidden}
                                onChange={(e) => handleUpdateAssertion(idx, 'is_hidden', e.target.checked)}
                                className="rounded text-primary focus:ring-primary h-3.5 w-3.5"
                              />
                              Hidden Check
                            </label>
                            <button
                              type="button"
                              onClick={() => handleRemoveAssertion(idx)}
                              className="text-gray-400 hover:text-red-500 p-1 cursor-pointer"
                              title="Remove assertion"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="col-span-1 md:col-span-2">
                            <label className="text-[11px] font-bold text-gray-600 block mb-1">
                              Test Title / Check Name
                            </label>
                            <input
                              type="text"
                              value={assertion.name}
                              onChange={(e) => handleUpdateAssertion(idx, 'name', e.target.value)}
                              placeholder="e.g. Check model R² score >= 0.85"
                              className="w-full text-xs p-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white"
                            />
                          </div>

                          <div>
                            <label className="text-[11px] font-bold text-gray-600 block mb-1">
                              Points Weight
                            </label>
                            <input
                              type="number"
                              value={assertion.points ?? 10}
                              onChange={(e) => handleUpdateAssertion(idx, 'points', Number(e.target.value))}
                              className="w-full text-xs p-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-[11px] font-bold text-gray-600 block mb-1">
                            Python Assertion Code (Evaluated in student namespace)
                          </label>
                          <textarea
                            rows={2}
                            value={assertion.assertion_code}
                            onChange={(e) => handleUpdateAssertion(idx, 'assertion_code', e.target.value)}
                            placeholder="assert 'model' in globals() and model.score(X_test, y_test) >= 0.80"
                            className="w-full font-mono text-xs p-2.5 bg-slate-900 text-emerald-400 rounded-lg focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="text-[11px] font-bold text-gray-600 block mb-1">
                            Hint / Explanation (shown to student if test fails)
                          </label>
                          <input
                            type="text"
                            value={assertion.explanation || ''}
                            onChange={(e) => handleUpdateAssertion(idx, 'explanation', e.target.value)}
                            placeholder="e.g. Ensure you train the classifier and achieve minimum 80% accuracy"
                            className="w-full text-xs p-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-blue-bg/40 p-3 rounded-xl border border-border">
                    <div className="text-xs text-slate-700 font-medium">
                      Define public sample test cases (visible to students) and hidden test cases (for grading only).
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      className="text-xs py-1.5 px-3 flex items-center gap-1 cursor-pointer"
                      onClick={handleAddTestCase}
                    >
                      <Plus size={14} />
                      Add Test Case
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {testCases.map((tc, idx) => (
                      <div
                        key={idx}
                        className={`border rounded-xl p-4 space-y-3 transition-colors ${
                          tc.is_hidden
                            ? 'border-amber-200 bg-amber-50/30'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-navy px-2 py-0.5 bg-gray-100 rounded-md">
                              Case #{idx + 1}
                            </span>
                            <span
                              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                tc.is_hidden
                                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                  : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              }`}
                            >
                              {tc.is_hidden ? '🔒 Hidden Case' : '👁️ Public Sample'}
                            </span>
                          </div>

                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!tc.is_hidden}
                                onChange={(e) => handleUpdateTestCase(idx, 'is_hidden', e.target.checked)}
                                className="rounded text-primary focus:ring-primary h-3.5 w-3.5"
                              />
                              Mark as Hidden
                            </label>
                            <button
                              type="button"
                              onClick={() => handleRemoveTestCase(idx)}
                              className="text-gray-400 hover:text-red-500 p-1 transition-colors cursor-pointer"
                              title="Remove test case"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] font-bold text-gray-600 block mb-1">
                              Input (JSON or text)
                            </label>
                            <textarea
                              rows={2}
                              value={tc.input}
                              onChange={(e) => handleUpdateTestCase(idx, 'input', e.target.value)}
                              placeholder='e.g. {"nums": [2, 7, 11, 15], "target": 9}'
                              className="w-full font-mono text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-gray-600 block mb-1">
                              Expected Output (JSON or text)
                            </label>
                            <textarea
                              rows={2}
                              value={tc.expected_output}
                              onChange={(e) => handleUpdateTestCase(idx, 'expected_output', e.target.value)}
                              placeholder="e.g. [0, 1]"
                              className="w-full font-mono text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white"
                            />
                          </div>
                        </div>

                        {!tc.is_hidden && (
                          <div>
                            <input
                              type="text"
                              value={tc.explanation || ''}
                              onChange={(e) => handleUpdateTestCase(idx, 'explanation', e.target.value)}
                              placeholder="Explanation for public test case (optional)"
                              className="w-full text-xs p-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: Reference Solution (For Function problems) */}
          {activeTab === 'solution' && !isNotebook && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-gray-200 p-4 rounded-xl space-y-2">
                <h4 className="text-xs font-bold text-navy flex items-center gap-1.5">
                  <Play size={14} className="text-emerald-600" />
                  Reference Python Solution (Recommended)
                </h4>
                <p className="text-xs text-gray-600">
                  Providing a working reference solution ensures all test cases are strictly correct before publishing to students.
                </p>
              </div>

              <div>
                <textarea
                  rows={10}
                  value={referenceSolution}
                  onChange={(e) => {
                    setReferenceSolution(e.target.value);
                    setRefValidationResult(null);
                  }}
                  className="w-full font-mono text-xs p-3 bg-slate-900 text-emerald-400 rounded-xl border border-gray-300 focus:outline-none"
                  placeholder="def two_sum(nums, target):&#10;    # Working reference implementation&#10;    lookup = {}&#10;    for i, num in enumerate(nums):&#10;        comp = target - num&#10;        if comp in lookup:&#10;            return [lookup[comp], i]&#10;        lookup[num] = i&#10;    return []"
                />
              </div>

              {problem?.id && (
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleValidateReferenceSolution}
                    disabled={isValidatingRef || !referenceSolution.trim()}
                    className="text-xs flex items-center gap-1.5 cursor-pointer"
                  >
                    <Play size={14} />
                    {isValidatingRef ? 'Validating Against Test Cases...' : 'Validate Reference Solution'}
                  </Button>

                  {refValidationResult && (
                    <div
                      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border ${
                        refValidationResult.is_valid
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}
                    >
                      {refValidationResult.is_valid ? (
                        <CheckCircle2 size={14} className="shrink-0" />
                      ) : (
                        <AlertCircle size={14} className="shrink-0" />
                      )}
                      {refValidationResult.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-5 py-3 bg-gray-50 shrink-0">
          <Button type="button" variant="dark" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <div className="flex items-center gap-2">
            {((!isNotebook && activeTab !== 'solution') || (isNotebook && activeTab !== 'testcases')) ? (
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  if (activeTab === 'details') {
                    setActiveTab(isNotebook ? 'datasets' : 'code');
                  } else if (activeTab === 'datasets') {
                    setActiveTab('code');
                  } else if (activeTab === 'code') {
                    setActiveTab('testcases');
                  } else if (activeTab === 'testcases') {
                    setActiveTab('solution');
                  }
                }}
              >
                Next Step →
              </Button>
            ) : null}

            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting
                ? 'Saving...'
                : isEditing
                ? 'Update Task'
                : 'Create Task'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default CodingProblemModal;
